import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  Divider,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ModelTraining,
  Assessment,
  Timeline,
  TrendingUp,
  CheckCircle,
  Warning,
  Error,
  Info,
  Speed,
  ShowChart as Accuracy,
  Refresh,
  Memory,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  RadialBarChart,
  RadialBar,
  ReferenceLine,
} from 'recharts';

import { CreditRiskAPI, formatPercentage } from '../services/api';
import { ModelPerformance, FeatureImportance } from '../types';

interface ModelInfoTabProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const ModelInfoTab: React.FC<ModelInfoTabProps> = ({ onNotification }) => {
  const [performance, setPerformance] = useState<ModelPerformance | null>(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedModel, setSelectedModel] = useState<'loan_default' | 'scorecard'>('loan_default');

  useEffect(() => {
    loadModelPerformance();
  }, [selectedModel]);

  const loadModelPerformance = async () => {
    setLoading(true);
    try {
      const response = await CreditRiskAPI.getModelPerformance(selectedModel);
      if (response.success && response.data) {
        setPerformance(response.data);
        onNotification(`Model performance data loaded for ${selectedModel === 'loan_default' ? 'Loan Default Prediction' : 'Credit Scorecard'}`, 'success');
      } else {
        onNotification(response.error?.message || 'Failed to load model performance', 'error');
      }
    } catch (error) {
      onNotification('An error occurred while loading model performance', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleModelChange = (event: SelectChangeEvent) => {
    setSelectedModel(event.target.value as 'loan_default' | 'scorecard');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getMetricColor = (metric: string, value: number) => {
    switch (metric) {
      case 'accuracy':
      case 'precision':
      case 'recall':
      case 'f1_score':
      case 'auc_roc':
      case 'auc_pr':
        if (value >= 0.9) return 'success';
        if (value >= 0.8) return 'warning';
        return 'error';
      case 'gini':
        if (value >= 0.6) return 'success';
        if (value >= 0.4) return 'warning';
        return 'error';
      default:
        return 'primary';
    }
  };

  const getMetricIcon = (metric: string) => {
    switch (metric) {
      case 'accuracy': return <Accuracy />;
      case 'precision': return <CheckCircle />;
      case 'recall': return <Assessment />;
      case 'f1_score': return <Speed />;
      case 'auc_roc': return <TrendingUp />;
      case 'auc_pr': return <Timeline />;
      case 'gini': return <ModelTraining />;
      case 'ks_statistic': return <Memory />;
      default: return <Info />;
    }
  };

  const formatConfusionMatrix = (matrix: number[][]) => {
    if (!matrix || matrix.length !== 2 || matrix[0].length !== 2) return [];

    return [
      { actual: 'No Default', predicted_no: matrix[0][0], predicted_yes: matrix[0][1] },
      { actual: 'Default', predicted_no: matrix[1][0], predicted_yes: matrix[1][1] },
    ];
  };

  if (loading && !performance) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={60} />
        <Box ml={2}>
          <Typography variant="h6">Loading Model Performance...</Typography>
          <Typography variant="body2" color="text.secondary">
            Fetching model metrics and evaluation data
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Model Performance Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive evaluation metrics and performance analysis of the credit risk model.
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="model-select-label">Select Model</InputLabel>
            <Select
              labelId="model-select-label"
              id="model-select"
              value={selectedModel}
              label="Select Model"
              onChange={handleModelChange}
              disabled={loading}
            >
              <MenuItem value="loan_default">Loan Default Prediction</MenuItem>
              <MenuItem value="scorecard">Credit Scorecard</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadModelPerformance}
            disabled={loading}
          >
            Refresh Metrics
          </Button>
        </Box>
      </Box>

      {performance ? (
        <Box>
          {/* Quick Metrics Overview */}
          <Grid container spacing={3} mb={3}>
            {performance.test_metrics && Object.entries({
              precision: 'Precision',
              recall: 'Recall',
              f1_score: 'F1 Score',
              auc_roc: 'AUC-ROC',
              gini: 'Gini Coefficient',
            }).filter(([key]) => performance.test_metrics[key as keyof typeof performance.test_metrics] !== undefined)
            .map(([key, label]) => {
              const value = performance.test_metrics[key as keyof typeof performance.test_metrics] as number;
              return (
                <Grid item xs={12} sm={6} md={2} key={key}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 1, width: 32, height: 32 }}>
                          {getMetricIcon(key)}
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                          {label}
                        </Typography>
                      </Box>
                      <Typography variant="h6" color={`${getMetricColor(key, value)}.main`}>
                        {formatPercentage(value)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={value * 100}
                        color={getMetricColor(key, value)}
                        sx={{ mt: 1, height: 4, borderRadius: 2 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Performance Analysis Tabs */}
          <Paper elevation={1}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Model Metrics" />
              <Tab label="ROC & PR Curves" />
              <Tab label="Feature Importance" />
              <Tab label="Confusion Matrix" />
              <Tab label="Cross Validation" />
              <Tab label="Calibration" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* Model Metrics */}
              {tabValue === 0 && (
                <Grid container spacing={3}>
                  {/* Metrics Comparison Table */}
                  <Grid item xs={12}>
                    <Card>
                      <CardHeader title="Performance Metrics Comparison" />
                      <CardContent>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Metric</TableCell>
                                <TableCell align="right">Training</TableCell>
                                <TableCell align="right">Validation</TableCell>
                                <TableCell align="right">Test</TableCell>
                                <TableCell align="center">Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries({
                                precision: 'Precision',
                                recall: 'Recall',
                                f1_score: 'F1 Score',
                                auc_roc: 'AUC-ROC',
                                auc_pr: 'AUC-PR',
                                gini: 'Gini Coefficient',
                                ks_statistic: 'KS Statistic',
                              }).filter(([key]) =>
                                performance.test_metrics[key as keyof typeof performance.test_metrics] !== undefined
                              ).map(([key, label]) => {
                                const trainValue = performance.training_metrics[key as keyof typeof performance.training_metrics] as number;
                                const valValue = performance.validation_metrics[key as keyof typeof performance.validation_metrics] as number;
                                const testValue = performance.test_metrics[key as keyof typeof performance.test_metrics] as number;
                                const overfit = Math.abs(trainValue - testValue) > 0.05;

                                return (
                                  <TableRow key={key}>
                                    <TableCell>{label}</TableCell>
                                    <TableCell align="right">{formatPercentage(trainValue)}</TableCell>
                                    <TableCell align="right">{formatPercentage(valValue)}</TableCell>
                                    <TableCell align="right">{formatPercentage(testValue)}</TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        icon={overfit ? <Warning /> : <CheckCircle />}
                                        label={overfit ? 'Overfitting' : 'Good'}
                                        color={overfit ? 'warning' : 'success'}
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Metrics Visualization */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Metrics Across Data Sets" />
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={Object.entries({
                              precision: 'Precision',
                              recall: 'Recall',
                              f1_score: 'F1 Score',
                              auc_roc: 'AUC-ROC',
                            }).filter(([key]) =>
                              performance.test_metrics[key as keyof typeof performance.test_metrics] !== undefined
                            ).map(([key, label]) => ({
                              metric: label,
                              Training: performance.training_metrics[key as keyof typeof performance.training_metrics] as number,
                              Validation: performance.validation_metrics[key as keyof typeof performance.validation_metrics] as number,
                              Test: performance.test_metrics[key as keyof typeof performance.test_metrics] as number,
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="metric" angle={-45} textAnchor="end" height={100} />
                            <YAxis domain={[0, 1]} />
                            <Tooltip formatter={(value) => formatPercentage(value as number)} />
                            <Legend />
                            <Bar dataKey="Training" fill="#8884d8" />
                            <Bar dataKey="Validation" fill="#82ca9d" />
                            <Bar dataKey="Test" fill="#ffc658" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Radial Performance Chart */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Model Performance Overview" />
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <RadialBarChart
                            cx="50%"
                            cy="50%"
                            innerRadius="20%"
                            outerRadius="80%"
                            data={[
                              ...(performance.test_metrics.precision ? [{
                                name: 'Precision',
                                value: performance.test_metrics.precision * 100,
                                fill: '#8884d8',
                              }] : []),
                              ...(performance.test_metrics.recall ? [{
                                name: 'Recall',
                                value: performance.test_metrics.recall * 100,
                                fill: '#ff7c7c',
                              }] : []),
                              ...(performance.test_metrics.f1_score ? [{
                                name: 'F1 Score',
                                value: performance.test_metrics.f1_score * 100,
                                fill: '#82ca9d',
                              }] : []),
                              ...(performance.test_metrics.auc_roc ? [{
                                name: 'AUC-ROC',
                                value: performance.test_metrics.auc_roc * 100,
                                fill: '#ffc658',
                              }] : []),
                            ]}
                          >
                            <RadialBar dataKey="value" cornerRadius={10} />
                            <Tooltip formatter={(value) => `${(value as number).toFixed(1)}%`} />
                          </RadialBarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* ROC & PR Curves */}
              {tabValue === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title={`ROC Curve (AUC = ${formatPercentage(performance.test_metrics.auc_roc)})`} />
                      <CardContent>
                        <ResponsiveContainer width="100%" height={350}>
                          <LineChart
                            data={performance.roc_curve.fpr.map((fpr, i) => ({
                              fpr: fpr,
                              tpr: performance.roc_curve.tpr[i],
                              threshold: performance.roc_curve.thresholds[i],
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="fpr"
                              domain={[0, 1]}
                              label={{ value: 'False Positive Rate', position: 'insideBottom', offset: -10 }}
                            />
                            <YAxis
                              domain={[0, 1]}
                              label={{ value: 'True Positive Rate', angle: -90, position: 'insideLeft' }}
                            />
                            <Tooltip
                              formatter={(value, name) => [
                                (value as number).toFixed(3),
                                name === 'tpr' ? 'True Positive Rate' : name === 'fpr' ? 'False Positive Rate' : 'Threshold'
                              ]}
                            />
                            <Line
                              type="monotone"
                              dataKey="tpr"
                              stroke="#8884d8"
                              strokeWidth={2}
                              dot={false}
                            />
                            <ReferenceLine
                              segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]}
                              stroke="#ff7300"
                              strokeDasharray="5 5"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title={`Precision-Recall Curve (AUC = ${formatPercentage(performance.test_metrics.auc_pr)})`} />
                      <CardContent>
                        <ResponsiveContainer width="100%" height={350}>
                          <LineChart
                            data={performance.pr_curve.recall.map((recall, i) => ({
                              recall: recall,
                              precision: performance.pr_curve.precision[i],
                              threshold: performance.pr_curve.thresholds[i],
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="recall"
                              domain={[0, 1]}
                              label={{ value: 'Recall', position: 'insideBottom', offset: -10 }}
                            />
                            <YAxis
                              domain={[0, 1]}
                              label={{ value: 'Precision', angle: -90, position: 'insideLeft' }}
                            />
                            <Tooltip
                              formatter={(value, name) => [
                                (value as number).toFixed(3),
                                name === 'precision' ? 'Precision' : name === 'recall' ? 'Recall' : 'Threshold'
                              ]}
                            />
                            <Line
                              type="monotone"
                              dataKey="precision"
                              stroke="#82ca9d"
                              strokeWidth={2}
                              dot={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Feature Importance */}
              {tabValue === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card>
                      <CardHeader title="Feature Importance Ranking" />
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart
                            data={performance.feature_importance
                              .sort((a, b) => b.importance - a.importance)
                              .slice(0, 10)
                            }
                            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="feature"
                              angle={-45}
                              textAnchor="end"
                              height={80}
                              interval={0}
                            />
                            <YAxis />
                            <Tooltip
                              formatter={(value) => [`${(value as number).toFixed(4)}`, 'Importance Score']}
                            />
                            <Bar dataKey="importance" fill="#1976d2" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card>
                      <CardHeader title="Feature Importance Table" />
                      <CardContent>
                        <TableContainer sx={{ maxHeight: 400 }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell>Feature</TableCell>
                                <TableCell align="right">Importance Score</TableCell>
                                <TableCell align="center">Relative Importance</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {performance.feature_importance
                                .sort((a, b) => b.importance - a.importance)
                                .map((feature, index) => (
                                  <TableRow key={feature.feature}>
                                    <TableCell>#{index + 1}</TableCell>
                                    <TableCell><strong>{feature.feature}</strong></TableCell>
                                    <TableCell align="right">{feature.importance.toFixed(4)}</TableCell>
                                    <TableCell align="center">
                                      <Box display="flex" alignItems="center" gap={1}>
                                        {(() => {
                                          const maxImportance = Math.max(...performance.feature_importance.map(f => f.importance));
                                          const relativePercentage = (feature.importance / maxImportance) * 100;
                                          return (
                                            <>
                                              <LinearProgress
                                                variant="determinate"
                                                value={relativePercentage}
                                                sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                                              />
                                              <Typography variant="caption">
                                                {relativePercentage.toFixed(0)}%
                                              </Typography>
                                            </>
                                          );
                                        })()}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Confusion Matrix */}
              {tabValue === 3 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Confusion Matrix" />
                      <CardContent>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell></TableCell>
                                <TableCell align="center" colSpan={2}>
                                  <strong>Predicted</strong>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell><strong>Actual</strong></TableCell>
                                <TableCell align="center">No Default</TableCell>
                                <TableCell align="center">Default</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {formatConfusionMatrix(performance.test_metrics.confusion_matrix).map((row) => (
                                <TableRow key={row.actual}>
                                  <TableCell><strong>{row.actual}</strong></TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      // Green for correct predictions (diagonal), pink for incorrect
                                      bgcolor: row.actual === 'No Default' ? '#e8f5e8' : '#ffebee',
                                      fontWeight: row.actual === 'No Default' ? 'bold' : 'normal'
                                    }}
                                  >
                                    {row.predicted_no.toLocaleString()}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      // Green for correct predictions (diagonal), pink for incorrect
                                      bgcolor: row.actual === 'Default' ? '#e8f5e8' : '#ffebee',
                                      fontWeight: row.actual === 'Default' ? 'bold' : 'normal'
                                    }}
                                  >
                                    {row.predicted_yes.toLocaleString()}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Box mt={3}>
                          <Typography variant="subtitle2" gutterBottom>
                            Matrix Analysis:
                          </Typography>
                          <List dense>
                            <ListItem>
                              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                              <ListItemText
                                primary="True Negatives (Correct No Default)"
                                secondary={`${performance.test_metrics.confusion_matrix[0][0].toLocaleString()} cases`}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon><Error color="error" /></ListItemIcon>
                              <ListItemText
                                primary="False Positives (Incorrect Default Prediction)"
                                secondary={`${performance.test_metrics.confusion_matrix[0][1].toLocaleString()} cases`}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon><Warning color="warning" /></ListItemIcon>
                              <ListItemText
                                primary="False Negatives (Missed Defaults)"
                                secondary={`${performance.test_metrics.confusion_matrix[1][0].toLocaleString()} cases`}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                              <ListItemText
                                primary="True Positives (Correct Default Prediction)"
                                secondary={`${performance.test_metrics.confusion_matrix[1][1].toLocaleString()} cases`}
                              />
                            </ListItem>
                          </List>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Classification Report" />
                      <CardContent>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Class</TableCell>
                                <TableCell align="right">Precision</TableCell>
                                <TableCell align="right">Recall</TableCell>
                                <TableCell align="right">F1-Score</TableCell>
                                <TableCell align="right">Support</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(performance.test_metrics.classification_report)
                                .filter(([key]) => !['accuracy', 'macro avg', 'weighted avg'].includes(key))
                                .map(([className, metrics]) => (
                                  <TableRow key={className}>
                                    <TableCell>
                                      <Chip
                                        label={className === '0' ? 'No Default' : className === '1' ? 'Default' : className}
                                        color={className === '0' ? 'success' : 'error'}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell align="right">{formatPercentage(metrics.precision)}</TableCell>
                                    <TableCell align="right">{formatPercentage(metrics.recall)}</TableCell>
                                    <TableCell align="right">{formatPercentage(metrics['f1-score'])}</TableCell>
                                    <TableCell align="right">{metrics.support.toLocaleString()}</TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Cross Validation */}
              {tabValue === 4 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Card>
                      <CardHeader title="Cross-Validation Scores" />
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart
                            data={performance.cross_validation_scores.map((score, index) => ({
                              fold: `Fold ${index + 1}`,
                              score: score,
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="fold" />
                            <YAxis domain={[0, 1]} />
                            <Tooltip formatter={(value) => formatPercentage(value as number)} />
                            <Area
                              type="monotone"
                              dataKey="score"
                              stroke="#8884d8"
                              fill="#8884d8"
                              fillOpacity={0.3}
                            />
                            <ReferenceLine
                              y={performance.cross_validation_scores.reduce((a, b) => a + b) / performance.cross_validation_scores.length}
                              stroke="#ff7300"
                              strokeDasharray="5 5"
                              label="Mean"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardHeader title="CV Statistics" />
                      <CardContent>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            Cross-validation evaluates model performance by training on different data splits,
                            providing a robust estimate of how well the model generalizes to unseen data.
                          </Typography>
                        </Alert>
                        <List>
                          <ListItem>
                            <ListItemText
                              primary="Mean Score"
                              secondary={formatPercentage(
                                performance.cross_validation_scores.reduce((a, b) => a + b) /
                                performance.cross_validation_scores.length
                              )}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Standard Deviation"
                              secondary={formatPercentage(
                                Math.sqrt(
                                  performance.cross_validation_scores.reduce((acc, score) => {
                                    const mean = performance.cross_validation_scores.reduce((a, b) => a + b) /
                                               performance.cross_validation_scores.length;
                                    return acc + Math.pow(score - mean, 2);
                                  }, 0) / performance.cross_validation_scores.length
                                )
                              )}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Min Score"
                              secondary={formatPercentage(Math.min(...performance.cross_validation_scores))}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Max Score"
                              secondary={formatPercentage(Math.max(...performance.cross_validation_scores))}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Calibration */}
              {tabValue === 5 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Card>
                      <CardHeader title="Calibration Plot" />
                      <CardContent>
                        <ResponsiveContainer width="100%" height={350}>
                          <ScatterChart
                            data={performance.calibration_curve.mean_predicted_value.map((predicted, i) => ({
                              predicted: predicted,
                              actual: performance.calibration_curve.fraction_of_positives[i],
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="predicted"
                              domain={[0, 1]}
                              label={{ value: 'Mean Predicted Probability', position: 'insideBottom', offset: -10 }}
                            />
                            <YAxis
                              domain={[0, 1]}
                              label={{ value: 'Fraction of Positives', angle: -90, position: 'insideLeft' }}
                            />
                            <Tooltip
                              formatter={(value, name) => [
                                (value as number).toFixed(3),
                                name === 'actual' ? 'Actual Fraction' : 'Predicted Probability'
                              ]}
                            />
                            <Scatter dataKey="actual" fill="#8884d8" />
                            <ReferenceLine
                              segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]}
                              stroke="#ff7300"
                              strokeDasharray="5 5"
                            />
                          </ScatterChart>
                        </ResponsiveContainer>
                        <Typography variant="body2" color="text.secondary" mt={1}>
                          Perfect calibration follows the diagonal line. Points below the line indicate overconfidence,
                          while points above indicate underconfidence.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardHeader title="Calibration Quality" />
                      <CardContent>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            Calibration measures how well predicted probabilities match actual outcomes.
                          </Typography>
                        </Alert>

                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <Assessment />
                            </ListItemIcon>
                            <ListItemText
                              primary="Calibration Assessment"
                              secondary="Based on reliability diagram analysis"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <Info />
                            </ListItemIcon>
                            <ListItemText
                              primary="Brier Score"
                              secondary="Lower is better for probability accuracy"
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Paper>
        </Box>
      ) : (
        <Alert severity="info">
          <Typography variant="body1">
            Model performance data is not available. Please ensure the model has been trained and evaluated.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ModelInfoTab;